/*
 * Browser Sentry, loaded after the page (ALU-663).
 *
 * `@sentry/nextjs` is ~80 KB gzipped. Importing it statically from
 * `instrumentation-client.ts` put it on the critical path of every page, so it
 * now arrives in its own chunk once the page has loaded and the browser is
 * idle (`scheduleSentryLoad`). Nothing is lost in between:
 *
 * - `installEarlyErrorCapture` listens for uncaught errors and unhandled
 *   rejections from the first script on, keeps them (bounded) and loads Sentry
 *   at once when one happens. After init the SDK's own handlers take over and
 *   the early ones are removed.
 * - `captureException` from React error boundaries is queued the same way.
 *
 * With no DSN in the build nothing is installed and Sentry never loads.
 * The init itself lives in `./sentry-boot` (the only module that imports the
 * SDK at runtime on the client).
 */
import type { captureRouterTransitionStart as CaptureRouterTransitionStart } from '@sentry/nextjs'
import { runAfterLoadIdle } from '@/lib/utils/after-load'

type SentryModule = typeof import('@sentry/nextjs')
type Job = (sentry: SentryModule) => void
type ExceptionHint = Parameters<SentryModule['captureException']>[1]

/** Early errors kept until Sentry is up; a crash loop must not grow memory. */
export const MAX_BUFFERED_ERRORS = 30

let sentry: SentryModule | null = null
let loading: Promise<SentryModule | null> | null = null
let failed = false
let removeEarlyListeners: (() => void) | null = null
const buffer: Job[] = []

/** The DSN is inlined at build time (literal `process.env.NEXT_PUBLIC_*` read). */
export function sentryConfigured(): boolean {
  return !!process.env.NEXT_PUBLIC_SENTRY_DSN?.trim()
}

function enqueue(job: Job): void {
  if (buffer.length >= MAX_BUFFERED_ERRORS) buffer.shift()
  buffer.push(job)
}

/** Loads and starts Sentry once; later calls share the same promise. */
export function loadSentry(): Promise<SentryModule | null> {
  if (sentry) return Promise.resolve(sentry)
  if (!sentryConfigured() || failed) return Promise.resolve(null)
  loading ??= import('./sentry-boot')
    .then(({ initSentry }) => {
      const loaded = initSentry()
      sentry = loaded
      // The SDK now has its own global handlers: drop the early ones first so
      // nothing is reported twice, then replay what they kept, in order.
      removeEarlyListeners?.()
      removeEarlyListeners = null
      for (const job of buffer.splice(0)) {
        try {
          job(loaded)
        } catch {
          /* a failing replay must not stop the rest */
        }
      }
      return loaded
    })
    .catch(() => {
      // Chunk failed to load (offline, blocked, stale deploy): no Sentry this
      // page load. The early listeners stay so the buffer keeps being bounded.
      failed = true
      loading = null
      return null
    })
  return loading
}

/** Starts Sentry when the page has loaded and the browser is idle. */
export function scheduleSentryLoad(): void {
  if (!sentryConfigured()) return
  runAfterLoadIdle(() => void loadSentry())
}

/** `Sentry.captureException`, queued until the SDK is up. */
export function captureException(error: unknown, hint?: ExceptionHint): void {
  if (sentry) {
    sentry.captureException(error, hint)
    return
  }
  if (!sentryConfigured() || failed) return
  enqueue((s) => void s.captureException(error, hint))
  void loadSentry()
}

/** Navigation tracing needs the SDK; transitions before it is up are not traced. */
export function captureRouterTransitionStart(
  ...args: Parameters<typeof CaptureRouterTransitionStart>
): void {
  sentry?.captureRouterTransitionStart(...args)
}

/**
 * Keeps errors that happen before the SDK is up. Returns a function that
 * removes the listeners (also called by the loader after init).
 */
export function installEarlyErrorCapture(target: Window = window): () => void {
  if (!sentryConfigured() || sentry) return () => undefined

  const onError = (event: ErrorEvent) => {
    // Resource load failures (img/script) bubble here without a message.
    if (!event.message && !event.error) return
    const error = event.error ?? new Error(event.message)
    enqueue((s) =>
      s.captureException(error, {
        mechanism: { type: 'auto.browser.global_handlers.onerror', handled: false },
      })
    )
    void loadSentry()
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason
    enqueue((s) =>
      s.captureException(reason, {
        mechanism: { type: 'auto.browser.global_handlers.onunhandledrejection', handled: false },
      })
    )
    void loadSentry()
  }
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  const remove = () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
  removeEarlyListeners = remove
  return remove
}

/** Test seam: forget the loaded SDK and the buffer. */
export function resetSentryLazyForTests(): void {
  sentry = null
  loading = null
  failed = false
  removeEarlyListeners?.()
  removeEarlyListeners = null
  buffer.length = 0
}
